import type { CallEndpoint, CallStats } from '@wtfalch/calls/browser';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type RoomCallBinding, startOrJoinRoomCall, withFreshDevice } from './roomCalls.ts';
import { callTeardownSettled, describeCallError, useCallEndpoint } from './useCallEndpoint.ts';
import { type LobbyState, useLobby } from './useLobby.ts';

/** How long a join may stay unanswered before the session is given up. */
const JOIN_TIMEOUT_MS = 30_000;

/** What a call is and where it lives, so the dock can name it and lead back. */
export type CallTarget = {
  roomId: string;
  /** The event's title, or the room's name. */
  title: string;
  /** The in-app path of the event page or room the call was started from. */
  path: string;
  /** A direct message: the call is "with" the person, not "in" a room. */
  direct?: boolean;
  /** Join with the camera on. Absent, the call starts audio-only. */
  video?: boolean;
  /** An event's own window. Absent, a new session lasts the huddle's twelve hours. */
  window?: { validFrom: number; expiresAt: number };
};

export type ActiveCall = CallTarget & {
  sessionId: string;
  calls: RoomCallBinding;
  startedAt: number;
};

/** What a call that its own member left leaves behind, for the call-ended page. */
export type EndedCall = {
  target: CallTarget;
  calls: RoomCallBinding;
  /** Whole minutes it ran, rounded up so a short call never reads "0 minutes". */
  minutes: number;
  /** The most people who were on it at once. */
  people: number;
};

export type StartResult =
  | { ok: true; sessionId: string; created: boolean }
  | { ok: false; error: string };

export type CallSessionValue = {
  /** The one call this tab is on, or null. */
  call: ActiveCall | null;
  /** The full call view is open on the page the call lives in. */
  focused: boolean;
  setFocused: (focused: boolean) => void;
  /** A start is in flight: Join is held until it lands. */
  starting: boolean;
  /** The media endpoint and its roster, once the join has started. */
  endpoint: CallEndpoint | null;
  stats: CallStats | null;
  joining: boolean;
  /** The guests waiting to be let in, once the join has landed. */
  lobby: LobbyState | null;
  /** The call this person just left, until they leave its page. Null for a
   *  call that failed or was ended by someone else: those use `notice`. */
  ended: EndedCall | null;
  dismissEnded: () => void;
  /** Why the last call ended without the person leaving it; shown by the dock. */
  notice: string | null;
  dismissNotice: () => void;
  /** Shows a notice on the dock for a failure with no local place to land,
   *  such as answering an incoming call while already on another one. */
  notify: (message: string) => void;
  start: (target: CallTarget, calls: RoomCallBinding) => Promise<StartResult>;
  leave: () => void;
};

/** Exported so `/__preview` can seat a made-up call without a calls service. */
export const CallSessionContext = createContext<CallSessionValue | null>(null);

/** The tab's call session. Null outside a `CallSessionProvider`. */
export function useCallSession(): CallSessionValue | null {
  return useContext(CallSessionContext);
}

const ENDED_NOTICE: Record<string, string> = {
  session_ended: 'The call has ended.',
  participant_removed: 'You were removed from the call.',
};

/**
 * Holds the one call a browser tab can be on, above the routes, so moving
 * between Today, Mail, Chat, Calendar and Settings never unmounts the media
 * connection. Event and room pages ask it to start a call and read it to show
 * the call as joined; the shell draws the dock from it.
 */
export function CallSessionProvider({ children }: { children: ReactNode }) {
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [focused, setFocused] = useState(false);
  const [starting, setStarting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [endedCall, setEnded] = useState<EndedCall | null>(null);
  const peakRef = useRef(0);
  const [live, setLive] = useState<{
    sessionId: string;
    endpoint: CallEndpoint;
    stats: CallStats | null;
    lobby: LobbyState;
  } | null>(null);
  // Refs as well as state: two quick presses must not both pass the guard.
  const callRef = useRef<ActiveCall | null>(null);
  const inflightRef = useRef<{ roomId: string; result: Promise<StartResult> } | null>(null);

  const begin = useCallback(async (target: CallTarget, calls: RoomCallBinding) => {
    setStarting(true);
    setNotice(null);
    setEnded(null);
    peakRef.current = 0;
    try {
      await callTeardownSettled();
      // Every join is a new seat: the server refuses a second one for a device
      // that has already held one in this call, even after it left.
      const seat = withFreshDevice(calls);
      const session = await startOrJoinRoomCall(
        seat.client,
        target.roomId,
        target.video ?? false,
        target.window,
      );
      const active = {
        ...target,
        sessionId: session.sessionId,
        calls: seat,
        startedAt: Date.now(),
      };
      callRef.current = active;
      setCall(active);
      setFocused(false);
      return { ok: true, ...session } as const;
    } catch (error) {
      return { ok: false, error: describeCallError(error, 'Could not start the call.') } as const;
    } finally {
      inflightRef.current = null;
      setStarting(false);
    }
  }, []);

  // Asking for the call this tab is already on (or is starting) answers with
  // that call; asking for another one is refused. Join never runs twice.
  const start = useCallback(
    (target: CallTarget, calls: RoomCallBinding): Promise<StartResult> => {
      const active = callRef.current;
      if (active) {
        return Promise.resolve(
          active.roomId === target.roomId
            ? { ok: true, sessionId: active.sessionId, created: false }
            : { ok: false, error: 'Leave the call you are on first.' },
        );
      }
      const pending = inflightRef.current;
      if (pending) {
        return pending.roomId === target.roomId
          ? pending.result
          : Promise.resolve({ ok: false, error: 'Leave the call you are on first.' });
      }
      const result = begin(target, calls);
      inflightRef.current = { roomId: target.roomId, result };
      return result;
    },
    [begin],
  );

  const end = useCallback((message: string | null) => {
    callRef.current = null;
    setCall(null);
    setFocused(false);
    setNotice(message);
  }, []);

  const leave = useCallback(() => {
    const active = callRef.current;
    if (active && peakRef.current > 0) {
      const { sessionId: _session, startedAt, calls, ...target } = active;
      setEnded({
        target,
        calls,
        minutes: Math.max(1, Math.ceil((Date.now() - startedAt) / 60_000)),
        people: peakRef.current,
      });
    }
    end(null);
  }, [end]);
  const failed = useCallback(
    (error: unknown) => end(describeCallError(error, 'Could not join the call.')),
    [end],
  );
  const ended = useCallback(
    (reason: string) => end(ENDED_NOTICE[reason] ?? 'The call connection was lost.'),
    [end],
  );

  const peopleNow = live?.stats?.participants.length ?? 0;
  const liveSession = live?.sessionId;
  useEffect(() => {
    if (call && liveSession === call.sessionId && peopleNow > peakRef.current) {
      peakRef.current = peopleNow;
    }
  }, [call, liveSession, peopleNow]);

  const value = useMemo<CallSessionValue>(() => {
    const mine = call && live?.sessionId === call.sessionId ? live : null;
    return {
      call,
      focused,
      setFocused,
      starting,
      endpoint: mine?.endpoint ?? null,
      stats: mine?.stats ?? null,
      lobby: mine?.stats ? mine.lobby : null,
      joining: call !== null && (mine?.stats ?? null) === null,
      notice,
      dismissNotice: () => setNotice(null),
      notify: setNotice,
      ended: endedCall,
      dismissEnded: () => setEnded(null),
      start,
      leave,
    };
  }, [call, focused, starting, live, notice, endedCall, start, leave]);

  return (
    <CallSessionContext.Provider value={value}>
      {children}
      {call && (
        <CallHost
          key={call.sessionId}
          call={call}
          onState={setLive}
          onFailed={failed}
          onEnded={ended}
        />
      )}
    </CallSessionContext.Provider>
  );
}

/**
 * The one place the media connection lives. Its unmount is the only leave:
 * `useCallEndpoint` tells the endpoint and the server when this goes away,
 * whether the person pressed Leave or the join failed.
 */
function CallHost({
  call,
  onState,
  onFailed,
  onEnded,
}: {
  call: ActiveCall;
  onState: (state: {
    sessionId: string;
    endpoint: CallEndpoint;
    stats: CallStats | null;
    lobby: LobbyState;
  }) => void;
  onFailed: (error: unknown) => void;
  onEnded: (reason: string) => void;
}) {
  const { calls, sessionId } = call;
  const { endpoint, stats, error } = useCallEndpoint({
    adapter: calls.adapter,
    store: calls.store,
    client: calls.client,
    sessionId,
    mode: call.video ? 'video' : 'audio',
    fetchImpl: calls.fetchImpl,
    endpointOptions: calls.endpointOptions,
  });

  const lobby = useLobby(calls.client, sessionId, stats !== null && stats.ended === null);

  useEffect(
    () => onState({ sessionId, endpoint, stats, lobby }),
    [onState, sessionId, endpoint, stats, lobby],
  );

  useEffect(() => {
    if (error != null) onFailed(error);
  }, [error, onFailed]);

  const endedReason = stats?.ended?.reason;
  useEffect(() => {
    if (endedReason && endedReason !== 'left') onEnded(endedReason);
  }, [endedReason, onEnded]);

  // A join that never answers must not leave the dock saying "Joining" forever.
  const joined = stats !== null;
  useEffect(() => {
    if (joined || error != null) return;
    const timer = setTimeout(
      () => onFailed(new Error('Could not connect to the call.')),
      JOIN_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [joined, error, onFailed]);

  // Closing or reloading the tab runs no unmount, so give the seat up here.
  // The control request goes first: it is the one that frees the seat.
  useEffect(() => {
    const onPageHide = () => {
      try {
        calls.client
          .leave(sessionId, { requestId: crypto.randomUUID() })
          .catch((leaveError: unknown) => console.error('Could not leave the call:', leaveError));
      } catch (leaveError) {
        console.error('Could not leave the call:', leaveError);
      }
      endpoint
        .leave()
        .catch((leaveError: unknown) => console.error('Could not stop the call:', leaveError));
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [calls.client, endpoint, sessionId]);

  return null;
}
